import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { useApi } from "../hooks/useApi";

// An audit after fieldwork (Audit Management 5A step 13): moving it through Review, Reporting and
// Closed, its report versions, and archiving after retention.
// - Review → Reporting needs every in-scope test signed off or approved as Not tested; Reporting →
//   Closed needs a report, and for NERC CIP its latest version approved. The server checks both.
// - Each report version is stored once and downloaded from that copy; from version 2 it lists what
//   changed since the previous one. A NERC CIP version is approved by the CIP Senior Manager or a
//   delegate holding the role that day (canApprove from the server).
// - A closed audit can be archived by a manager once every finding is closed and the retention
//   period has passed.

const BASE = "/security-insights/audits";
const messageOf = (err, fallback) => err?.response?.data?.message || fallback;
const showDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

// [status, label, primary]: the forward move is the primary button.
const MOVES = {
    Fieldwork: [["Review", "Move to review", true]],
    Review: [["Fieldwork", "Back to fieldwork", false], ["Reporting", "Move to reporting", true]],
    Reporting: [["Review", "Back to review", false], ["Closed", "Close audit", true]],
};

const APPROVAL_LABEL = { NotRequired: "No approval needed", Pending: "Awaiting CIP Senior Manager approval", Approved: "Approved" };
const APPROVAL_STYLE = {
    NotRequired: "bg-gray-50 text-gray-600 border-gray-200",
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    Approved: "bg-green-50 text-green-700 border-green-200",
};

export const useReportDownload = () => {
    const apiCall = useApi();
    return useCallback(async (report) => {
        const blob = await apiCall("GET", `${BASE}/${report.engagementId}/reports/${report.id}/file`, null, false, "blob");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `AUD-${report.engagementId}-report-v${report.version}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    }, [apiCall]);
};

// A Download link that shows a spinner and ignores clicks until onDownload's promise settles, so a
// slow file is not requested again by repeated clicks.
export const DownloadButton = ({ onDownload }) => {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        if (busy) return;
        setBusy(true);
        try { await onDownload(); } finally { setBusy(false); }
    };
    return (
        <button type="button" onClick={click} disabled={busy} aria-busy={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-white
                bg-gradient-to-b from-indigo-500 to-indigo-700 border-b-[3px] border-indigo-900 shadow-md
                hover:from-indigo-400 hover:to-indigo-600 hover:shadow-lg
                active:translate-y-[2px] active:border-b active:shadow-sm transition-all
                disabled:from-gray-400 disabled:to-gray-500 disabled:border-gray-600 disabled:cursor-wait">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {busy ? "Downloading…" : "Download"}
        </button>
    );
};

// auditName: shown first when the row is listed outside its audit (awaiting approval).
const ReportRow = ({ report, onDownload, onApprove, auditName = null }) => (
    <li data-testid={`audit-report-v${report.version}`} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {auditName && <span className="text-gray-800 font-semibold">{auditName}</span>}
        <span className="font-semibold text-gray-800 w-20 shrink-0">Version {report.version}</span>
        <span className="text-gray-600">{showDate(report.generatedAt)} by {report.generatedByName}</span>
        {report.version > 1 && <span className="text-gray-500">{report.changeCount} change{report.changeCount === 1 ? "" : "s"} since version {report.version - 1}</span>}
        <span className={`px-2 py-0.5 rounded-full border font-semibold ${APPROVAL_STYLE[report.approvalState] || APPROVAL_STYLE.NotRequired}`}>
            {APPROVAL_LABEL[report.approvalState] || report.approvalState}
        </span>
        {report.approvedByName && <span className="text-gray-600">by {report.approvedByName} on {showDate(report.approvedAt)}</span>}
        <span className="ml-auto flex gap-2">
            <DownloadButton onDownload={() => onDownload(report)} />
            {report.canApprove && (
                <button type="button" onClick={() => onApprove(report)} className="px-2 py-0.5 rounded bg-green-600 text-white">Approve</button>
            )}
        </span>
    </li>
);

// Pending NERC CIP reports this person may approve today; nothing when there are none.
export const AuditReportsAwaitingApproval = () => {
    const apiCall = useApi();
    const download = useReportDownload();
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const load = useCallback(() => apiCall("GET", `${BASE}/reports/awaiting-approval`)
        .then(list => setItems(Array.isArray(list) ? list : []))
        .catch(() => setItems([])), [apiCall]);
    useEffect(() => { load(); }, [load]);

    const approve = async (report) => {
        setError(null);
        try {
            await apiCall("POST", `${BASE}/${report.engagementId}/reports/${report.id}/approve`);
            await load();
        } catch (err) {
            setError(messageOf(err, "The report could not be approved."));
        }
    };
    const get = (report) => download(report).catch(err => setError(messageOf(err, "The report could not be downloaded.")));

    if (items.length === 0 && !error) return null;
    return (
        <div className="bg-white border border-amber-200 rounded-lg text-xs" data-testid="audit-reports-awaiting-approval">
            <div className="p-3 border-b border-amber-200">
                <div className="text-sm font-semibold text-gray-800">Audit reports awaiting your approval ({items.length})</div>
                <div className="text-gray-500">As CIP Senior Manager or delegate. Read the report before approving it.</div>
            </div>
            <ul className="divide-y divide-gray-100">
                {items.map(r => <ReportRow key={r.id} report={r} auditName={r.engagementName} onDownload={get} onApprove={approve} />)}
            </ul>
            {error && <div className="p-3 text-red-600" role="alert">{error}</div>}
        </div>
    );
};

const AuditReports = ({ engagement, canArchive = false, onEngagementChanged }) => {
    const apiCall = useApi();
    const download = useReportDownload();
    const base = `${BASE}/${engagement.id}`;
    const [reports, setReports] = useState(null);
    const [retention, setRetention] = useState(null);
    const [error, setError] = useState(null);
    const afterFieldwork = engagement.status === "Reporting" || engagement.status === "Closed";

    const load = useCallback(() => {
        if (!afterFieldwork) return Promise.resolve();
        return Promise.all([
            apiCall("GET", `${base}/reports`).then(list => setReports(Array.isArray(list) ? list : [])).catch(() => setReports([])),
            engagement.status === "Closed"
                ? apiCall("GET", `${base}/retention`).then(setRetention).catch(() => setRetention(null))
                : Promise.resolve(),
        ]);
    }, [apiCall, base, afterFieldwork, engagement.status]);
    useEffect(() => { load(); }, [load]);

    const run = async (action, fallback, reloadEngagement = false) => {
        setError(null);
        try {
            await action();
            if (reloadEngagement) await onEngagementChanged?.();
            else await load();
        } catch (err) {
            setError(messageOf(err, fallback));
        }
    };

    if (engagement.status === "Planning") return null;
    const moves = engagement.canManage ? MOVES[engagement.status] || [] : [];
    return (
        <div className="text-xs space-y-1" data-testid={`audit-reports-${engagement.id}`}>
            {moves.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {moves.map(([status, label, primary]) => (
                        <button key={status} type="button"
                            onClick={() => run(() => apiCall("POST", `${base}/status`, { status }), "The audit could not be moved.", true)}
                            className={primary ? "px-2 py-0.5 rounded bg-indigo-600 text-white" : "px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-100"}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
            {afterFieldwork && reports && (
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-600 mr-auto">Report ({reports.length} version{reports.length === 1 ? "" : "s"})</span>
                        {engagement.canManage && (
                            <button type="button" onClick={() => run(() => apiCall("POST", `${base}/reports`), "The report could not be generated.")}
                                className="px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">
                                {reports.length === 0 ? "Generate report" : "Generate new version"}
                            </button>
                        )}
                    </div>
                    {reports.length === 0
                        ? <div className="text-gray-400">No report generated yet.</div>
                        : (
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                                {reports.map(r => (
                                    <ReportRow key={r.id} report={r}
                                        onDownload={(rep) => run(() => download(rep), "The report could not be downloaded.")}
                                        onApprove={(rep) => run(() => apiCall("POST", `${base}/reports/${rep.id}/approve`), "The report could not be approved.")} />
                                ))}
                            </ul>
                        )}
                </div>
            )}
            {retention && (
                <div className="flex flex-wrap items-center gap-2 text-gray-600" data-testid={`audit-retention-${engagement.id}`}>
                    {retention.archivedAt
                        ? <span>Archived on {showDate(retention.archivedAt)}.</span>
                        : retention.blocker
                            ? <span>{retention.blocker}</span>
                            : <span>Kept for {retention.retentionYears} years: can be archived from {showDate(retention.archivableFrom)}.</span>}
                    {canArchive && !retention.archivedAt && !retention.blocker && new Date(retention.archivableFrom) <= new Date() && (
                        <button type="button" onClick={() => run(() => apiCall("POST", `${base}/archive`), "The audit could not be archived.", true)}
                            className="px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-100">Archive</button>
                    )}
                </div>
            )}
            {error && <div className="text-red-600" role="alert">{error}</div>}
        </div>
    );
};

export default AuditReports;
